import { httpsCallable } from 'firebase/functions'
import { functions } from '../utils/firebase/firebase'
import axios from 'axios'

export const checkAccessTokenValidity = async token => {
  try {
    const url = `https://graph.facebook.com/v22.0/me?access_token=${token}`
    const response = await axios.get(url)
    if (response.status === 200) {
      return true
    }
  } catch (error) {
    console.log(error)
  }
  return false
}

export const fetchAccountData = async (token, usernameIG) => {
  let returnData = null
  try {
    const api = `https://graph.facebook.com/v22.0/me/accounts?access_token=${token}&fields=id,name,access_token,picture,instagram_business_account{username,id,ig_id,profile_picture_url}&limit=50`
    const response = await axios.get(api)
    for (const account of response.data.data) {
      if (account.instagram_business_account) {
        const instagramAccount = account.instagram_business_account
        const username = instagramAccount.username
        if (username.toUpperCase() === usernameIG.toUpperCase()) {
          const accountId = instagramAccount.id
          const igId = instagramAccount.ig_id
          const profilePictureUrl = instagramAccount.profile_picture_url

          const instagramObject = {
            username: username,
            accountId: accountId,
            igId: igId,
            profilePictureUrl: profilePictureUrl,
          }

          returnData = instagramObject
          break
        }
      }
    }
  } catch (error) {
    console.log(error)
  }
  return returnData
}

export const fetchInstagramMedia = async (
  accountId,
  accessToken,
  postLimit,
  nextPageCursor,
  passData,
  shortcode = null,
) => {
  let returnData = passData
  returnData.nextPageCursor = nextPageCursor

  try {
    returnData.errorPage = 0

    let foundShortcodePost = false

    while (!foundShortcodePost) {
      const api = `https://graph.facebook.com/v22.0/${accountId}/media?access_token=${accessToken}&fields=caption,media_url,timestamp,comments_count,permalink,thumbnail_url,shortcode,is_comment_enabled,ig_id,like_count&limit=${postLimit}&after=${returnData.nextPageCursor}`
      const response = await axios.get(api)
      const apiData = response.data

      if (apiData.data.length > 0) {
        const prevdata = passData.instagramMediaData
        const currdata = apiData.data

        // Merge prevdata and currdata, ensuring uniqueness by ig_id using Map
        const mergedData = new Map(
          [...prevdata, ...currdata].map(item => [item.ig_id, item]),
        )
        returnData.instagramMediaData = [...mergedData.values()]

        if (shortcode) {
          const shortcodePost = currdata.find(
            item => item.shortcode === shortcode,
          )
          if (shortcodePost) {
            returnData.postSelected = shortcodePost
            foundShortcodePost = true
          }
        } else {
          foundShortcodePost = true
        }

        if (!returnData.postSelected) {
          returnData.postSelected = currdata[0]
        }
        returnData.nextPageCursor = apiData.paging.cursors.after

        if (!shortcode && apiData.data.length < postLimit) {
          returnData.hasMore = false
          break
        }

        if (!apiData.paging.cursors.after) {
          break
        }

        if (foundShortcodePost) {
          break
        }
      } else {
        returnData.hasMore = false
        break
      }
    }
  } catch (error) {
    console.log(error)
    returnData.errorPage = 2
  }

  return returnData
}

export const fetchInstagramComments = async (
  postId,
  accessToken,
  pickLimit,
) => {
  let returnData = []

  const fetchData = async () => {
    let nextPageCursor = ''
    let totalFetched = 0
    let remainingLimit = pickLimit // Initialize remainingLimit with pickLimit

    while (totalFetched < pickLimit) {
      let apiUrl = `https://graph.facebook.com/v22.0/${postId}/comments?access_token=${accessToken}&fields=id,text,username,like_count,timestamp,mentions,from&limit=${
        remainingLimit > 50 ? 50 : remainingLimit
      }&after=${nextPageCursor}`

      const response = await axios.get(apiUrl)
      const comments = response.data.data
      if (comments.length > 0) {
        const modifiedComments = comments.map(comment => {
          const commentText = comment.text
          const mentionedUsernames = commentText.match(/@(\w+)/g)
          const cleanedUsernames = mentionedUsernames
            ? [
                ...new Set(
                  mentionedUsernames.map(username => username.slice(1)),
                ),
              ]
            : []
          const mentionCount = cleanedUsernames.length

          return {
            ...comment,
            mentions: cleanedUsernames,
            mention_count: mentionCount,
          }
        })

        returnData.push(...modifiedComments)
        totalFetched += modifiedComments.length
      }

      remainingLimit -= comments.length

      // Check if there's a next page
      if (
        remainingLimit > 0 &&
        response.data.paging &&
        response.data.paging.next
      ) {
        nextPageCursor = response.data.paging.cursors.after
      } else {
        break
      }
    }
  }

  try {
    await fetchData()
  } catch (error) {
    // Only retry if no data was fetched during the first attempt
    if (returnData.length === 0) {
      try {
        await fetchData()
      } catch (secondError) {
        console.log('Second attempt failed:', secondError)
      }
    } else {
      console.log('First attempt failed:', error)
    }
  }
  return returnData
}

export const getInstagramProfileData = async (
  searchInput,
  imageOnly = false,
  isUsername = true,
) => {
  try {
    const getInstagramProfileImageData = httpsCallable(
      functions,
      'getInstagramProfileImageData',
    )
    const result = await getInstagramProfileImageData({
      searchInput,
      imageOnly,
      isUsername,
    })
    const data = result.data
    if (data.error) {
      if (data.error === 404 && !imageOnly) {
        // for display account before login, 404 Not Found handle for invalid username
        return data
      }
      throw new Error(data.error)
    }
    return data
  } catch (error) {
    return { data: null, error: error }
  }
}

export const mergeArrays = async (arr1, arr2) => {
  return arr1.map(obj1 => {
    const matchedObj = arr2.find(obj2 => obj2.name === obj1.name)
    return { ...obj1, ...(matchedObj && { data: matchedObj.data }) }
  })
}

export const getRandomObjectAndRemove = array => {
  const randomIndex = Math.floor(Math.random() * array.length)
  const selectedObject = array[randomIndex]
  const newArray = array.filter((_, index) => index !== randomIndex)
  return { selectedObject, newArray }
}

export const getModifiedParticipants = (data, limit) => {
  let availableObjects = [...data]
  let selectedObjects = []

  if (data.length > limit) {
    for (let i = 0; i < limit; i++) {
      const { selectedObject, newArray } =
        getRandomObjectAndRemove(availableObjects)
      selectedObjects.push(selectedObject)
      availableObjects = newArray
    }
  } else {
    selectedObjects = availableObjects
  }

  return selectedObjects
}
